import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import './App.css';

const SERVER = window.location.origin;

interface Recording {
  name: string;
  url: string;
  size: number;
  date: string;
}

export default function App() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [cameraConnected, setCameraConnected] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceDetected, setVoiceDetected] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [activeTab, setActiveTab] = useState<'live' | 'recordings' | 'settings'>('live');
  const [settings, setSettings] = useState({ whatsappInstance: '', whatsappToken: '', whatsappNumber: '', voiceThreshold: 40 });
  const [saved, setSaved] = useState('');

  const socketRef = useRef<Socket | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const sock = io(SERVER);
    socketRef.current = sock;

    sock.on('auth-ok', (data: any) => {
      setAuthed(true);
      setRecording(data.recording);
      setCameraConnected(data.cameraConnected);
    });
    sock.on('auth-fail', () => setAuthError(true));
    sock.on('camera-status', (d: any) => setCameraConnected(d.connected));
    sock.on('recording-status', (d: any) => setRecording(d.recording));
    sock.on('voice-detected', (v: boolean) => setVoiceDetected(v));
    sock.on('audio-level', (l: number) => setAudioLevel(l));
    sock.on('frame', (data: string) => {
      if (imgRef.current) imgRef.current.src = `data:image/jpeg;base64,${data}`;
    });

    return () => { sock.disconnect(); };
  }, []);

  useEffect(() => {
    if (authed) fetchRecordings();
  }, [authed, activeTab]);

  async function fetchRecordings() {
    const r = await fetch('/api/recordings');
    setRecordings(await r.json());
  }

  async function deleteRecording(name: string) {
    await fetch(`/api/recordings/${name}`, { method: 'DELETE' });
    fetchRecordings();
  }

  function login() {
    setAuthError(false);
    socketRef.current?.emit('dashboard-join', password);
  }

  function toggleRecording() {
    if (recording) socketRef.current?.emit('stop-recording');
    else socketRef.current?.emit('start-recording');
  }

  async function saveSettings() {
    await fetch('/api/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        whatsappInstance: settings.whatsappInstance,
        whatsappToken: settings.whatsappToken,
        whatsappNumber: settings.whatsappNumber,
        voiceThreshold: settings.voiceThreshold
      })
    });
    setSaved('Settings saved!');
    setTimeout(() => setSaved(''), 3000);
  }

  function formatSize(bytes: number) {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (!authed) {
    return (
      <div className="login-screen">
        <div className="login-box">
          <div className="login-logo">🔒</div>
          <h2>CamSaaS</h2>
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && login()}
          />
          {authError && <p className="error">Wrong password</p>}
          <button onClick={login}>Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <span className="logo">📹 CamSaaS</span>
        <div className="status-badges">
          <span className={`badge ${cameraConnected ? 'green' : 'red'}`}>
            {cameraConnected ? '● LIVE' : '○ OFFLINE'}
          </span>
          {recording && <span className="badge red blink">⏺ REC</span>}
          {voiceDetected && <span className="badge orange blink">🎤 VOICE</span>}
        </div>
      </div>

      <div className="tabs">
        {(['live', 'recordings', 'settings'] as const).map(tab => (
          <button key={tab} className={`tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'live' ? '📺 Live' : tab === 'recordings' ? '🗂 Recordings' : '⚙️ Settings'}
          </button>
        ))}
      </div>

      {activeTab === 'live' && (
        <div className="live-tab">
          <div className="stream-box">
            {cameraConnected
              ? <img ref={imgRef} className="stream-img" alt="Live feed" />
              : <div className="no-signal">📵<br />Camera Offline<br /><small>Open app on phone</small></div>
            }
          </div>
          <div className="audio-bar-wrap">
            <span>🎤</span>
            <div className="audio-bar-bg">
              <div className="audio-bar-fill" style={{ width: `${audioLevel}%`, background: audioLevel > 60 ? '#ff4444' : audioLevel > 30 ? '#ffaa00' : '#00cc88' }} />
            </div>
            <span>{audioLevel}%</span>
          </div>
          <button className={`rec-btn ${recording ? 'recording' : ''}`} onClick={toggleRecording} disabled={!cameraConnected}>
            {recording ? '⏹ Stop Recording' : '⏺ Start Recording'}
          </button>
        </div>
      )}

      {activeTab === 'recordings' && (
        <div className="recordings-tab">
          <button className="refresh-btn" onClick={fetchRecordings}>🔄 Refresh</button>
          {recordings.length === 0
            ? <div className="empty">No recordings yet</div>
            : recordings.map(r => (
              <div className="rec-item" key={r.name}>
                <div className="rec-info">
                  <span className="rec-name">📹 {r.name}</span>
                  <span className="rec-meta">{formatSize(r.size)} · {new Date(r.date).toLocaleString('en-PK', { timeZone: 'Asia/Karachi' })}</span>
                </div>
                <div className="rec-actions">
                  <a href={r.url} download className="btn-dl">⬇ Download</a>
                  <button className="btn-del" onClick={() => deleteRecording(r.name)}>🗑</button>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="settings-tab">
          <h3>WhatsApp Voice Alerts</h3>
          <label>Green API Instance ID</label>
          <input value={settings.whatsappInstance} onChange={e => setSettings({ ...settings, whatsappInstance: e.target.value })} placeholder="e.g. 1101234567" />
          <label>Green API Token</label>
          <input value={settings.whatsappToken} onChange={e => setSettings({ ...settings, whatsappToken: e.target.value })} placeholder="Token..." />
          <label>Alert WhatsApp Number</label>
          <input value={settings.whatsappNumber} onChange={e => setSettings({ ...settings, whatsappNumber: e.target.value })} placeholder="923001234567" />

          <h3>Voice Sensitivity</h3>
          <label>Threshold: {settings.voiceThreshold}%</label>
          <input type="range" min={10} max={90} value={settings.voiceThreshold} onChange={e => setSettings({ ...settings, voiceThreshold: Number(e.target.value) })} />
          <small>Lower = more sensitive &nbsp;|&nbsp; Higher = only loud sounds</small>

          <button className="save-btn" onClick={saveSettings}>Save Settings</button>
          {saved && <p className="success">{saved}</p>}

          <hr />
          <h3>Camera App URL</h3>
          <p>Open this on phone browser:</p>
          <code>{window.location.origin}/camera</code>
        </div>
      )}
    </div>
  );
}
